import React, { FC, useContext } from 'react';
import { ascComparator, T } from 'src/shared/helpers';
import styled from 'styled-components';

import { BasicButton, PrimaryButton } from '../../../../../../widgets/buttons';
import { MessageDialog, useDialogManager } from '../../../../../../widgets/dialog';
import { showInfoNotification } from '../../../../../../widgets/notificationToaster';
import { ZTooltip } from '../../../../../../widgets/ztooltip';
import { useProductConfigurationState } from 'src/components/pages/productConfiguration/state';
import usePreview3DStore, { Preview3DStoreContext } from 'src/components/widgets/preview3D/store';
import Screenshot from 'src/shared/material_icons/image/photo_camera.svg';
import Merge from 'src/shared/material_icons/maps/merge.svg';
import Copy from 'src/shared/material_icons/content/content_copy.svg';
import Plus from 'src/shared/material_icons/content/add.svg';
import Minus from 'src/shared/material_icons/content/remove.svg';
import { ComposerAttributeTreeElement } from '../../../configurator-composer/composer/components/composerAttributeTreeElement';
import useComposerStore from '../../../configurator-composer/composer/composerStore';
import { ComposerCopyOptionsAttributeDialog } from '../../../configurator-composer/composer/dialogs/composerCopyOptionsAttributeDialog';
import ComposerGenerateAIAttributeDialog from '../../../configurator-composer/composer/dialogs/composerGenerateAIAttributeDialog';
import ComposerAddGalleryDialog, { ComposerBuildCardKey, ComposerUseCaseKey } from '../../../configurator-composer/composer/dialogs/composerAddGalleryDialog';
import ComposerImportAttributeDialog from '../../../configurator-composer/composer/dialogs/composerImportAttributeDialog';
import { ComposerMergeAttributeDialog } from '../../../configurator-composer/composer/dialogs/composerMergeAttributeDialog';
import ComposerRetargetAttributeDialog from '../../../configurator-composer/composer/dialogs/composerRetargetAttributeDialog';
import {
    getRetargetObjectsAndMaterials,
    createSecureClonedName,
    randomCode,
    retargetMapToActions,
    setActionSceneElementType,
    sortByDisplay
} from '../../../configurator-composer/composer/helpers/composerHelpers';
import { ComposerAttributeNodeAdapter } from '../../../configurator-composer/composer/helpers/composerNodesAdapter';
import { isMacPlatform } from '../../../configurator-composer/composer/helpers/composerSelectionHint';
import {
    canDropComposerAttributeInside,
    COMPOSER_ATTRIBUTE_DRAG_TYPE,
    ComposerAttributeDropPosition,
    moveComposerAttributeNode
} from '../../../configurator-composer/composer/helpers/composerTreeDnD';
import type { TreeNodeHook } from '../../tree';
import {
    ComposerAttributeNode,
    ComposerAttributeOptionNode,
    ComposerAttributePreviewType,
    ComposerAttributeType,
    ComposerOptionActionNode,
    ComposerOptionActionsType
} from './composerAttributesModels';
import {
    ComposerIconButton,
    ComposerLinkButton,
    ComposerList,
    ComposerEmptyState,
    ComposerPanel,
    ComposerScrollBody,
    ComposerSearch,
    ComposerSearchRow,
    ComposerToolbar,
    ComposerTreeToolsRow
} from '../composerWorkspace';

const TreeHint = styled.p`
    margin: 10px 8px 0;
    color: var(--composer-faint, #677078);
    font-size: 11px;
    line-height: 16px;
`;

const TreeDropSurface = styled(ComposerScrollBody)<{ $rootDropTarget: boolean }>`
    position: relative;

    &::after {
        position: sticky;
        right: 8px;
        bottom: 2px;
        left: 8px;
        display: ${(props) => (props.$rootDropTarget ? 'block' : 'none')};
        height: 3px;
        background: var(--composer-accent, #e8650a);
        border-radius: 999px;
        box-shadow: 0 0 0 1px var(--composer-panel, #fff);
        content: '';
        pointer-events: none;
    }
`;

type AttributeDropTarget = {
    id: string | null;
    position?: ComposerAttributeDropPosition;
};

const ComposerAttributes: FC<{ state: TreeNodeHook<ComposerAttributeNode> }> = ({ state }) => {
    const [
        currentAttributes,
        selectedOptions,
        storeMeshList,
        storeMaterialsList,
        selectAttributes,
        updateAttribute,
        removeAttributes,
        cloneAttributes,
        updateOption,
        validateAttributes,
        selectOptions,
        setAttributeFace
    ] = useComposerStore((state) => [
        state.attributes,
        state.selectedOptions,
        state.sceneObjectList,
        state.sceneMaterialList,
        state.selectAttributes,
        state.updateAttribute,
        state.removeAttributes,
        state.cloneAttributes,
        state.updateOption,
        state.validateAttributes,
        state.selectOptions,
        state.setAttributeFace
    ]);
    const [sceneEditor] = usePreview3DStore((x) => [x.sceneEditor]);

    const preview3DStore = useContext(Preview3DStoreContext);
    const { state: productState } = useProductConfigurationState();
    const { showDialog, closeDialog } = useDialogManager();

    const { filter, setFilter, selectedIds, filteredData, updateNode, findNodeById, removeNodes, treeState, setSelectedIds } = state;
    const [draggedAttributeId, setDraggedAttributeId] = React.useState<string | null>(null);
    const [attributeDropTarget, setAttributeDropTarget] = React.useState<AttributeDropTarget | null>(null);

    const storeSelectedAttributes = useComposerStore((state) => state.selectedAttributes);
    React.useEffect(() => {
        if (storeSelectedAttributes && storeSelectedAttributes.length > 0) {
            const attributeIds = storeSelectedAttributes.map((attr) => attr.id);
            const currentIds = [...selectedIds].sort().join(',');
            const storeIds = [...attributeIds].sort().join(',');

            if (currentIds !== storeIds) {
                // Use queueMicrotask to ensure this happens after tree reinit
                queueMicrotask(() => {
                    setSelectedIds(attributeIds);
                });
            }
        } else if (selectedIds.length > 0 && (!storeSelectedAttributes || storeSelectedAttributes.length === 0)) {
            // Store has no selection, clear local selection
            setSelectedIds([]);
        }
    }, [storeSelectedAttributes, currentAttributes, selectedIds]); // Re-sync when attributes change (tree reinit)

    const selectedAttributes = React.useMemo(() => {
        if (!selectedIds || selectedIds.length === 0) return [];
        return selectedIds.map((id) => findNodeById(id)).filter((x) => x) as ComposerAttributeNode[];
    }, [selectedIds, findNodeById]);
    const canCombineAttributes = React.useMemo(
        () => currentAttributes.filter((attribute) => attribute.type === ComposerAttributeType.Attribute).length >= 2,
        [currentAttributes]
    );
    const sceneMeshList = storeMeshList.filter((x) => sceneEditor!.isRealMesh(x)).sort(ascComparator((item) => item.name ?? '')) ?? [];
    const sceneMaterialList =
        storeMaterialsList.filter((x) => x.id != 'default material' && x.name != 'default material').sort(ascComparator((item) => item.name ?? '')) ?? [];
    const getHomeGroup = () => {
        let current: ComposerAttributeNode | undefined = selectedAttributes[0];
        while (current) {
            if (current.type === ComposerAttributeType.AttributeGroup) return current;
            const parentID: string | undefined = current.parentID;
            current = parentID ? currentAttributes.find((attribute) => attribute.id === parentID) : undefined;
        }
        return null;
    };

    const createSeedAction = (
        attributeID: string,
        optionID: string,
        type: ComposerOptionActionsType,
        selector: string,
        value = ''
    ): ComposerOptionActionNode => {
        const action = ComposerAttributeNodeAdapter.toNodeAction(attributeID, optionID, ComposerAttributeNodeAdapter.createNewAction());
        action.type = type;
        action.selector = selector;
        action.value = value;
        setActionSceneElementType(action);
        return action;
    };

    const createSeedOption = (
        attribute: ComposerAttributeNode,
        name: string,
        index: number,
        actions: Array<{ type: ComposerOptionActionsType; selector?: string; value?: string }> = []
    ): ComposerAttributeOptionNode => {
        const option = ComposerAttributeNodeAdapter.toNodeOption(attribute.id, ComposerAttributeNodeAdapter.createNewOption(index));
        option.name = name;
        option.code = `${attribute.code}-${index + 1}`;
        option.description = '';
        option.displayOrder = index;
        option.isDefault = index === 0;
        option.actions = actions.map((action, actionIndex) => ({
            ...createSeedAction(attribute.id, option.id, action.type, action.selector ?? '', action.value ?? ''),
            displayOrder: actionIndex
        }));
        return option;
    };

    const createAttributeNode = (name: string, code: string, previewType = ComposerAttributePreviewType.Rectangle) => {
        const homeGroup = getHomeGroup();
        const attribute = ComposerAttributeNodeAdapter.toNode(ComposerAttributeNodeAdapter.createNew());
        attribute.name = name;
        attribute.code = code;
        attribute.previewType = previewType;
        attribute.isNew = true;
        attribute.parentID = homeGroup?.id;
        attribute.parentIDNumeric = homeGroup?.idNumeric ?? -1;
        attribute.parentType = homeGroup?.type;
        attribute.camera = homeGroup?.camera;
        attribute.displayOrder =
            Math.max(-1, ...currentAttributes.filter((candidate) => candidate.parentID === homeGroup?.id).map((candidate) => candidate.displayOrder)) + 1;
        return attribute;
    };

    const landOnAttribute = (attribute: ComposerAttributeNode, face: 'options' | 'settings' = 'options') => {
        updateAttribute(attribute);
        validateAttributes();
        setSelectedIds([attribute.id]);
        selectAttributes([attribute]);
        selectOptions(attribute.options[0] ? [attribute.options[0]] : []);
        setAttributeFace(face);
    };

    const seedUseCase = (key: ComposerUseCaseKey, printingMethodID: string) => {
        const meshSelector = sceneMeshList[0]?.id || sceneMeshList[0]?.name || '';
        const materialIDs = sceneMaterialList.slice(0, 3).map((material) => material.id);
        let attribute: ComposerAttributeNode;

        if (key === 'color') {
            attribute = createAttributeNode(T._('Colour', 'Admin'), 'COL', ComposerAttributePreviewType.Circle);
            attribute.options = materialIDs.map((materialID, index) =>
                createSeedOption(attribute, sceneMaterialList[index]?.name || T._(`Material ${index + 1}`, 'Admin'), index, [
                    { type: ComposerOptionActionsType.SET_OBJECT_MATERIAL, selector: meshSelector, value: materialID }
                ])
            );
        } else if (key === 'showhide') {
            attribute = createAttributeNode(T._('Version', 'Admin'), 'VER');
            attribute.options = [
                createSeedOption(attribute, T._('With it', 'Admin'), 0, [{ type: ComposerOptionActionsType.SHOW_OBJECTS, selector: meshSelector }]),
                createSeedOption(attribute, T._('Without it', 'Admin'), 1, [{ type: ComposerOptionActionsType.HIDE_OBJECTS, selector: meshSelector }])
            ];
        } else if (key === 'engraving') {
            attribute = createAttributeNode(T._('Engraving', 'Admin'), 'ENG');
            attribute.options = [
                createSeedOption(attribute, T._('No engraving', 'Admin'), 0),
                createSeedOption(
                    attribute,
                    T._('Custom text', 'Admin'),
                    1,
                    printingMethodID ? [{ type: ComposerOptionActionsType.SET_PRINTING_METHOD, value: printingMethodID }] : []
                )
            ];
        } else if (key === 'image') {
            attribute = createAttributeNode(T._('Custom image', 'Admin'), 'IMG');
            attribute.options = [
                createSeedOption(attribute, T._('No image', 'Admin'), 0),
                createSeedOption(
                    attribute,
                    T._('Uploaded image', 'Admin'),
                    1,
                    materialIDs[0] ? [{ type: ComposerOptionActionsType.SET_DESIGN_MATERIAL, selector: meshSelector, value: materialIDs[0] }] : []
                )
            ];
        } else if (key === 'state') {
            attribute = createAttributeNode(T._('State', 'Admin'), 'STA');
            attribute.options = [
                createSeedOption(attribute, T._('Closed', 'Admin'), 0, [{ type: ComposerOptionActionsType.HIDE_OBJECTS, selector: meshSelector }]),
                createSeedOption(attribute, T._('Open', 'Admin'), 1, [{ type: ComposerOptionActionsType.SHOW_OBJECTS, selector: meshSelector }])
            ];
        } else {
            attribute = createAttributeNode(T._('Trim pack', 'Admin'), 'TRIM');
            attribute.options = [
                createSeedOption(attribute, T._('Standard', 'Admin'), 0, [
                    { type: ComposerOptionActionsType.SET_OBJECT_MATERIAL, selector: meshSelector, value: materialIDs[0] || '' },
                    { type: ComposerOptionActionsType.SHOW_OBJECTS, selector: meshSelector }
                ]),
                createSeedOption(attribute, T._('Premium', 'Admin'), 1, [
                    { type: ComposerOptionActionsType.SET_OBJECT_MATERIAL, selector: meshSelector, value: materialIDs[1] || materialIDs[0] || '' },
                    { type: ComposerOptionActionsType.SHOW_OBJECTS, selector: meshSelector }
                ])
            ];
        }

        landOnAttribute(attribute);
        if (!meshSelector || ((key === 'color' || key === 'image' || key === 'compound') && materialIDs.length === 0)) {
            showInfoNotification({
                message: T._('The structure was created, but some seeded actions need a real scene object or material before they can run.', 'Admin')
            });
        }
    };

    const buildStructure = (key: ComposerBuildCardKey) => {
        if (key === 'attribute') {
            landOnAttribute(createAttributeNode(T._('New attribute', 'Admin'), randomCode('attribute')), 'settings');
            return;
        }
        if (key === 'group') {
            const group = ComposerAttributeNodeAdapter.toGroupNode(ComposerAttributeNodeAdapter.createNewGroup());
            group.name = T._('New group', 'Admin');
            group.isNew = true;
            group.displayOrder = Math.max(-1, ...currentAttributes.filter((attribute) => !attribute.parentID).map((attribute) => attribute.displayOrder)) + 1;
            landOnAttribute(group, 'settings');
            return;
        }
        if (key === 'step') {
            const homeGroup = getHomeGroup();
            const step = ComposerAttributeNodeAdapter.toStepNode(ComposerAttributeNodeAdapter.createNewStep());
            step.name = T._('New step', 'Admin');
            step.isNew = true;
            step.parentID = homeGroup?.id;
            step.parentIDNumeric = homeGroup?.idNumeric ?? -1;
            step.parentType = homeGroup?.type;
            step.displayOrder =
                Math.max(-1, ...currentAttributes.filter((attribute) => attribute.parentID === homeGroup?.id).map((attribute) => attribute.displayOrder)) + 1;
            landOnAttribute(step, 'settings');
        }
    };

    const onAttributeButtonClick = (action: string) => {
        switch (action) {
            case 'add':
                showDialog(
                    'composerAddGallery',
                    <ComposerAddGalleryDialog
                        onCancel={() => closeDialog('composerAddGallery')}
                        onUseCase={(key, printingMethodID) => {
                            closeDialog('composerAddGallery');
                            seedUseCase(key, printingMethodID);
                        }}
                        onBuild={(key) => {
                            closeDialog('composerAddGallery');
                            if (key === 'import') onAttributeButtonClick('import');
                            else buildStructure(key);
                        }}
                        onAI={() => {
                            closeDialog('composerAddGallery');
                            onAttributeButtonClick('add-ai');
                        }}
                        onUnavailable={(name) =>
                            showInfoNotification({
                                message: T._(`${name} is visible in the prototype but is not connected to a product preset API yet.`, 'Admin')
                            })
                        }
                    />
                );
                break;
            case 'add-ai':
                showDialog(
                    'composerGenerateAIAttribute',
                    <Preview3DStoreContext.Provider value={preview3DStore}>
                        <ComposerGenerateAIAttributeDialog
                            onClose={() => closeDialog('composerGenerateAIAttribute')}
                            materialList={sceneMaterialList}
                            meshList={sceneMeshList}
                            sceneEditor={preview3DStore?.getState().sceneEditor}
                            onConfirm={(attributes) => {
                                closeDialog('composerGenerateAIAttribute');
                                if (!attributes) return;
                                attributes.forEach((attribute) => {
                                    attribute.isNew = true;
                                    updateAttribute(attribute);
                                });
                            }}
                        />
                    </Preview3DStoreContext.Provider>
                );
                break;
            case 'remove':
                //confirm dialog
                if (!selectedAttributes || selectedAttributes.length === 0) {
                    console.warn('No attribute selected for removal');
                    return;
                }
                showDialog(
                    'confirmDialog',
                    <MessageDialog
                        title={T._('Confirm Removal', 'Admin')}
                        message={
                            T._('Are you sure you want to remove the selected attribute(s)? ', 'Admin') + selectedAttributes.map((attr) => attr.name).join(', ')
                        }
                        buttons={[
                            { label: T._('Cancel', 'Admin'), outline: true, onClick: () => closeDialog('confirmDialog') },
                            {
                                label: T._('Remove', 'Admin'),
                                destructive: true,
                                onClick: () => {
                                    closeDialog('confirmDialog');
                                    const ids = selectedAttributes.map((attr) => attr.id);
                                    const childIds = selectedAttributes.flatMap((attr) => getNodeDescendants(attr)).map((child) => child.id);
                                    removeNodes(childIds);
                                    removeAttributes(childIds);
                                    removeNodes(ids);
                                    removeAttributes(ids);
                                    validateAttributes();
                                    setSelectedIds([]);
                                    selectAttributes(null);
                                }
                            }
                        ]}
                    />
                );
                break;
            case 'duplicate':
                if (!selectedAttributes || selectedAttributes.length === 0) {
                    console.warn('No attribute selected for duplication');
                    return;
                }
                const attributesToClone = [...selectedAttributes];
                const descendantsToClone: ComposerAttributeNode[] = [];

                const findDescendants = (node: ComposerAttributeNode) => {
                    const children = getNodeChildren(node);
                    for (const child of children) {
                        descendantsToClone.push(child);
                        findDescendants(child);
                    }
                };

                selectedAttributes.forEach(findDescendants);

                const allToClone = [...descendantsToClone, ...attributesToClone];
                const clonedAttributes = cloneAttributes(allToClone);
                validateAttributes();
                const clonedSelection = clonedAttributes.slice(Math.max(0, clonedAttributes.length - attributesToClone.length));
                setSelectedIds(clonedSelection.map((attribute) => attribute.id));
                selectAttributes(clonedSelection);
                selectOptions(null);
                break;
            case 'import':
                showDialog(
                    'composerImportAttribute',
                    <ComposerImportAttributeDialog
                        salesChannelID={productState.salesChannelID}
                        materialsList={sceneMaterialList}
                        objectsList={sceneMeshList}
                        onConfirm={(result) => {
                            closeDialog('composerImportAttribute');
                            if (!result || !result.selectedAttributes || result.selectedAttributes.length === 0) {
                                return;
                            }
                            const imported: ComposerAttributeNode[] = [];
                            for (const srcAttr of result.selectedAttributes) {
                                // Clone the attribute with new IDs
                                const newAttr: ComposerAttributeNode = {
                                    ...srcAttr,
                                    id: MPlaza.generateUUID(),
                                    idNumeric: -1,
                                    code: randomCode(srcAttr.code || 'attribute'),
                                    isNew: true,
                                    displayOrder: -1,
                                    parentID: undefined,
                                    parentIDNumeric: -1,
                                    parentType: undefined,
                                    links: [],
                                    linkOperation: undefined,
                                    options: srcAttr.options.map((opt, idx) => {
                                        const clonedOpt = {
                                            ...opt,
                                            id: MPlaza.generateUUID(),
                                            idNumeric: -1,
                                            attributeID: '', // Will be set below
                                            links: [],
                                            linkOperation: undefined,
                                            displayOrder: idx,
                                            actions: (opt.actions || []).map((action) => ({
                                                ...action,
                                                id: MPlaza.generateUUID(),
                                                attributeID: '',
                                                optionID: ''
                                            }))
                                        };
                                        return clonedOpt;
                                    })
                                };

                                // Fix references after generating the new attribute ID
                                newAttr.options = newAttr.options.map((opt) => ({
                                    ...opt,
                                    attributeID: newAttr.id,
                                    actions: opt.actions.map((a) => ({
                                        ...a,
                                        attributeID: newAttr.id,
                                        optionID: opt.id
                                    }))
                                }));

                                // Remap actions using object/material matches
                                const allActions = newAttr.options.flatMap((opt) => opt.actions || []);
                                const remapped = retargetMapToActions(allActions, result.materialMatches, result.objectMatches);
                                newAttr.options.forEach((opt) => {
                                    opt.actions = (opt.actions || []).map((a) => {
                                        const found = remapped.find((r) => r.id === a.id);
                                        return found || a;
                                    });
                                });

                                updateAttribute(newAttr);
                                imported.push(newAttr);
                            }
                            validateAttributes();
                            setSelectedIds(imported.map((a) => a.id));
                            selectAttributes(imported);
                        }}
                    />
                );
                break;
            case 'retarget':
                const currentAttr = selectedAttributes?.[0] || null;
                if (!currentAttr) {
                    console.warn('Retargeting attributes is only available for a single selected attribute');
                    return;
                }
                const options = currentAttr.options || [];
                if (options.length === 0) {
                    console.warn('Selected attribute has no options to retarget');
                    return;
                }
                const actions = options.flatMap((opt) => opt.actions || []);
                const { objectIds, materialIds } = getRetargetObjectsAndMaterials(actions);
                showDialog(
                    'composerRetargetAttribute',
                    <ComposerRetargetAttributeDialog
                        materialsList={sceneMaterialList}
                        objectsList={sceneMeshList}
                        usedMaterialsList={sceneMaterialList.filter((m) => materialIds.includes(m.id))}
                        usedObjectsList={sceneMeshList.filter((o) => objectIds.includes(o.id))}
                        onConfirm={(data) => {
                            if (!data) {
                                closeDialog('composerRetargetAttribute');
                                return;
                            }
                            closeDialog('composerRetargetAttribute');
                            const { materialMap, objectMap } = data;
                            retargetMapToActions(actions, materialMap, objectMap).forEach((action) => {
                                options.forEach((opt) => {
                                    const actionIndex = opt.actions?.findIndex((a) => a.id === action.id) ?? -1;
                                    if (actionIndex >= 0) {
                                        opt.actions![actionIndex] = action;
                                    }
                                });
                            });
                            currentAttr.options = options;
                            updateAttribute(currentAttr);
                        }}
                    />
                );
                break;
            case 'merge':
                const attributes = currentAttributes.filter((attr) => attr.type === ComposerAttributeType.Attribute);
                if (attributes.length < 2) {
                    return;
                }
                showDialog(
                    'composerMergeAttributes',
                    <ComposerMergeAttributeDialog
                        attributesList={attributes}
                        onConfirm={(result) => {
                            closeDialog('composerMergeAttributes');
                            if (!result) return;
                            const { newAttribute, deleteSourceAttributes, attributesToMerge } = result;
                            if (!newAttribute || !attributesToMerge || attributesToMerge.length < 2) return;
                            if (!newAttribute.name.trim() || !newAttribute.code.trim()) return;

                            // Flatten and clone options from all selected attributes
                            const allOptions = attributesToMerge.flatMap((attr) => attr.options);
                            const optionCodeCounts = new Map<string, number>();
                            const mergedOptions: ComposerAttributeOptionNode[] = allOptions.map((opt, index) => {
                                // Deduplicate option codes with _1, _2 suffixes
                                let code = opt.code;
                                const count = optionCodeCounts.get(code) || 0;
                                if (count > 0) {
                                    code = `${opt.code}_${count}`;
                                }
                                optionCodeCounts.set(opt.code, count + 1);
                                const optionID = MPlaza.generateUUID();

                                return {
                                    ...opt,
                                    id: optionID,
                                    idNumeric: -1,
                                    code,
                                    attributeID: newAttribute.id,
                                    isDefault: index === 0,
                                    displayOrder: index,
                                    links: [],
                                    linkOperation: undefined,
                                    actions: opt.actions.map((action, actionIndex) => ({
                                        ...action,
                                        id: MPlaza.generateUUID(),
                                        attributeID: newAttribute.id,
                                        optionID,
                                        displayOrder: actionIndex,
                                        isNew: true
                                    }))
                                };
                            });

                            newAttribute.options = mergedOptions;
                            newAttribute.isNew = true;
                            newAttribute.displayOrder = -1;
                            updateAttribute(newAttribute);
                            if (deleteSourceAttributes && attributesToMerge && attributesToMerge.length > 0) {
                                const ids = [...attributesToMerge.flatMap((attribute) => getNodeDescendants(attribute)), ...attributesToMerge].map(
                                    (attribute) => attribute.id
                                );
                                removeNodes(ids);
                                removeAttributes(ids);
                            }
                            validateAttributes();
                            setSelectedIds([newAttribute.id]);
                            selectAttributes([newAttribute]);
                        }}
                    />
                );
                break;
            case 'copy':
                showDialog(
                    'composerCopyAttribute',
                    <ComposerCopyOptionsAttributeDialog
                        attributesList={currentAttributes.filter((attr) => attr.type === ComposerAttributeType.Attribute)}
                        onConfirm={(result) => {
                            closeDialog('composerCopyAttribute');
                            if (!result) return;
                            const { optionsToCopy, targetAttribute, replaceExistingOptions } = result;
                            if (!targetAttribute || !optionsToCopy || optionsToCopy.length === 0) return;
                            const existingOptions = replaceExistingOptions ? [] : [...(targetAttribute.options || [])];
                            const usedCodes = existingOptions.map((option) => option.code);
                            const copiedOptions = optionsToCopy.map((option, index) => {
                                const optionID = MPlaza.generateUUID();
                                const code = createSecureClonedName(option.code, usedCodes);
                                usedCodes.push(code);
                                return {
                                    ...option,
                                    id: optionID,
                                    idNumeric: -1,
                                    code,
                                    attributeID: targetAttribute.id,
                                    displayOrder: existingOptions.length + index,
                                    isDefault: existingOptions.length === 0 && index === 0,
                                    isNew: true,
                                    actions: option.actions.map((action, actionIndex) => ({
                                        ...action,
                                        id: MPlaza.generateUUID(),
                                        attributeID: targetAttribute.id,
                                        optionID,
                                        displayOrder: actionIndex,
                                        isNew: true
                                    }))
                                };
                            });
                            const copiedPricings = optionsToCopy.flatMap((option, index) => {
                                const sourceAttribute = currentAttributes.find((attribute) => attribute.id === option.attributeID);
                                return (sourceAttribute?.pricings || [])
                                    .filter((pricing) => pricing.selectedOptionGuid === option.id)
                                    .map((pricing) => ({
                                        ...pricing,
                                        attributePricingID: 0,
                                        selectedOptionID: -1,
                                        selectedOptionGuid: copiedOptions[index].id
                                    }));
                            });
                            const updatedAttribute: ComposerAttributeNode = {
                                ...targetAttribute,
                                options: [...existingOptions, ...copiedOptions],
                                pricings: replaceExistingOptions ? copiedPricings : [...targetAttribute.pricings, ...copiedPricings]
                            };
                            updateAttribute(updatedAttribute);
                            updateNode(updatedAttribute.id, updatedAttribute);
                            validateAttributes();
                            setSelectedIds([updatedAttribute.id]);
                            selectAttributes([updatedAttribute]);
                        }}
                    />
                );
                break;
            default:
                console.warn('Unknown action:', action);
        }
    };
    const parentNodes = React.useMemo(() => filteredData.filter((a) => (a.isNew && !a.parentID) || !a.parentID), [filteredData]);
    const nodes = React.useMemo(() => parentNodes.sort((a, b) => sortByDisplay(parentNodes, a, b)), [parentNodes]);

    const getNodeChildren = (node: ComposerAttributeNode) => {
        return filteredData.filter((attr) => attr.parentID === node.id);
    };

    const getNodeDescendants = (node: ComposerAttributeNode): ComposerAttributeNode[] =>
        getNodeChildren(node).flatMap((child) => [child, ...getNodeDescendants(child)]);

    const selectSingleAttribute = (attribute: ComposerAttributeNode) => {
        setSelectedIds([attribute.id]);
        selectAttributes([attribute]);
        selectOptions(attribute.type === ComposerAttributeType.Attribute && attribute.options.length > 0 ? [attribute.options[0]] : []);
    };

    const removeAttributeRows = (attributesToRemove: ComposerAttributeNode[]) => {
        showDialog(
            'confirmDialog',
            <MessageDialog
                title={T._('Confirm Removal', 'Admin')}
                message={
                    T._('Are you sure you want to remove the selected item(s)? ', 'Admin') + attributesToRemove.map((attribute) => attribute.name).join(', ')
                }
                buttons={[
                    { label: T._('Cancel', 'Admin'), outline: true, onClick: () => closeDialog('confirmDialog') },
                    {
                        label: T._('Remove', 'Admin'),
                        destructive: true,
                        onClick: () => {
                            closeDialog('confirmDialog');
                            const descendants = attributesToRemove.flatMap((attribute) => getNodeDescendants(attribute));
                            const ids = [...descendants, ...attributesToRemove].map((attribute) => attribute.id);
                            removeNodes(ids);
                            removeAttributes(ids);
                            validateAttributes();
                            setSelectedIds([]);
                            selectAttributes(null);
                            selectOptions(null);
                        }
                    }
                ]}
            />
        );
    };

    const retargetAttributeRow = (attribute: ComposerAttributeNode) => {
        const options = attribute.options || [];
        if (options.length === 0) return;

        const actions = options.flatMap((option) => option.actions || []);
        const { objectIds, materialIds } = getRetargetObjectsAndMaterials(actions);
        showDialog(
            'composerRetargetAttribute',
            <ComposerRetargetAttributeDialog
                materialsList={sceneMaterialList}
                objectsList={sceneMeshList}
                usedMaterialsList={sceneMaterialList.filter((material) => materialIds.includes(material.id))}
                usedObjectsList={sceneMeshList.filter((object) => objectIds.includes(object.id))}
                onConfirm={(data) => {
                    closeDialog('composerRetargetAttribute');
                    if (!data) return;

                    const { materialMap, objectMap } = data;
                    const remappedActions = retargetMapToActions(actions, materialMap, objectMap);
                    const updatedAttribute = {
                        ...attribute,
                        options: options.map((option) => ({
                            ...option,
                            actions: option.actions.map((action) => remappedActions.find((candidate) => candidate.id === action.id) ?? action)
                        }))
                    };
                    updateAttribute(updatedAttribute);
                    updateNode(updatedAttribute.id, updatedAttribute);
                    validateAttributes();
                    selectSingleAttribute(updatedAttribute);
                }}
            />
        );
    };

    const onAttributeRowAction = (action: 'edit' | 'links' | 'duplicate' | 'retarget' | 'remove', attribute: ComposerAttributeNode) => {
        if (action === 'edit') {
            selectSingleAttribute(attribute);
            setAttributeFace('settings');
            return;
        }
        if (action === 'links') {
            selectSingleAttribute(attribute);
            setAttributeFace('links');
            return;
        }
        if (action === 'duplicate') {
            const descendants: ComposerAttributeNode[] = [];
            const collectDescendants = (current: ComposerAttributeNode) => {
                getNodeChildren(current).forEach((child) => {
                    descendants.push(child);
                    collectDescendants(child);
                });
            };
            collectDescendants(attribute);
            const cloned = cloneAttributes([...descendants, attribute]);
            validateAttributes();
            const clonedRow = cloned[cloned.length - 1];
            setSelectedIds(clonedRow ? [clonedRow.id] : []);
            selectAttributes(clonedRow ? [clonedRow] : null);
            selectOptions(null);
            return;
        }
        if (action === 'retarget') {
            selectSingleAttribute(attribute);
            retargetAttributeRow(attribute);
            return;
        }
        removeAttributeRows([attribute]);
    };
    const toggleAttributeEnabled = (attribute: ComposerAttributeNode) => {
        const updatedAttribute = { ...attribute, enabled: attribute.enabled === false };
        updateAttribute(updatedAttribute);
        updateNode(updatedAttribute.id, updatedAttribute);
        validateAttributes();
    };
    const getVisibleStructureRows = () => {
        const visible: ComposerAttributeNode[] = [];
        const visit = (node: ComposerAttributeNode) => {
            visible.push(node);
            if (node.type === ComposerAttributeType.Attribute || node.expanded) return;
            getNodeChildren(node)
                .sort((left, right) => sortByDisplay(filteredData, left, right))
                .forEach(visit);
        };
        nodes.forEach(visit);
        return visible;
    };

    const showMixedSelectionMessage = () =>
        showInfoNotification({
            message: T._('Select only rows of the same kind. Mixing groups, steps and attributes would duplicate nested content more than once.', 'Admin')
        });

    const nodeSelectHandler = (event: React.MouseEvent, node: ComposerAttributeNode) => {
        const ctrlKey = event.ctrlKey || event.metaKey;
        const nodeFromStore = currentAttributes.find((attribute) => attribute.id === node.id);
        if (!nodeFromStore) return;

        if (event.shiftKey && selectedIds.length > 0) {
            const visibleRows = getVisibleStructureRows();
            const anchor = visibleRows.findIndex((candidate) => candidate.id === selectedIds[selectedIds.length - 1]);
            const end = visibleRows.findIndex((candidate) => candidate.id === node.id);
            if (anchor < 0 || end < 0) return;
            const range = visibleRows.slice(Math.min(anchor, end), Math.max(anchor, end) + 1);
            const expectedType = range[0]?.type;
            if (range.some((candidate) => candidate.type !== expectedType)) {
                showMixedSelectionMessage();
                return;
            }
            const selectedRange = range
                .map((candidate) => currentAttributes.find((attribute) => attribute.id === candidate.id))
                .filter(Boolean) as ComposerAttributeNode[];
            setSelectedIds(selectedRange.map((attribute) => attribute.id));
            selectAttributes(selectedRange);
            selectOptions(null);
            return;
        }

        if (ctrlKey) {
            if (!selectedIds.includes(node.id) && selectedAttributes[0] && selectedAttributes[0].type !== nodeFromStore.type) {
                showMixedSelectionMessage();
                return;
            }
            const nextSelection = selectedIds.includes(node.id)
                ? selectedAttributes.filter((attribute) => attribute.id !== node.id)
                : [...selectedAttributes, nodeFromStore];
            setSelectedIds(nextSelection.map((attribute) => attribute.id));
            selectAttributes(nextSelection);
            selectOptions(nextSelection.length === 1 && nextSelection[0].options[0] ? [nextSelection[0].options[0]] : []);
            return;
        }

        setSelectedIds([node.id]);
        selectAttributes([nodeFromStore]);
        selectOptions(nodeFromStore.options[0] ? [nodeFromStore.options[0]] : []);
    };

    const moveNodeHandler = (node: ComposerAttributeNode, direction: 'up' | 'down') => {
        const siblings = filteredData
            .filter((n) => n.parentID === node.parentID)
            .sort((a, b) =>
                sortByDisplay(
                    filteredData.filter((n) => n.parentID === node.parentID),
                    a,
                    b
                )
            );

        const currentIndex = siblings.findIndex((n) => n.id === node.id);
        const swapIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        if (swapIndex >= 0 && swapIndex < siblings.length) {
            const swapNode = siblings[swapIndex];
            const currentOrder = node.displayOrder;

            updateNode(node.id, { ...node, displayOrder: swapNode.displayOrder });
            updateNode(swapNode.id, { ...swapNode, displayOrder: currentOrder });
            updateAttribute({ ...node, displayOrder: swapNode.displayOrder });
            updateAttribute({ ...swapNode, displayOrder: currentOrder });
            validateAttributes();
        }
    };

    const dragStartHandler = (e: React.DragEvent<HTMLButtonElement>, node: ComposerAttributeNode) => {
        e.stopPropagation();
        if (
            node.type === ComposerAttributeType.Attribute ||
            node.type === ComposerAttributeType.AttributeStep ||
            node.type === ComposerAttributeType.AttributeGroup
        ) {
            e.dataTransfer.setData(COMPOSER_ATTRIBUTE_DRAG_TYPE, node.id);
            e.dataTransfer.setData('text/plain', node.id);
            e.dataTransfer.effectAllowed = 'move';
            setDraggedAttributeId(node.id);
            setAttributeDropTarget(null);
            const draggedNode = currentAttributes.find((attribute) => attribute.id === node.id) ?? node;
            setSelectedIds([draggedNode.id]);
            selectAttributes([draggedNode]);
            selectOptions(draggedNode.options[0] ? [draggedNode.options[0]] : []);
        } else {
            e.preventDefault();
        }
    };

    const getDropPosition = (event: React.DragEvent<HTMLDivElement>, target: ComposerAttributeNode): ComposerAttributeDropPosition | null => {
        const dragged = currentAttributes.find((attribute) => attribute.id === draggedAttributeId);
        if (!dragged) return null;

        const bounds = event.currentTarget.getBoundingClientRect();
        const pointerRatio = bounds.height > 0 ? (event.clientY - bounds.top) / bounds.height : 0.5;
        const canDropInside = canDropComposerAttributeInside(dragged, target);
        const position = canDropInside && pointerRatio >= 0.25 && pointerRatio <= 0.75 ? 'inside' : pointerRatio < 0.5 ? 'before' : 'after';

        return moveComposerAttributeNode(currentAttributes, dragged.id, target.id, position) === currentAttributes ? null : position;
    };

    const dragOverHandler = (e: React.DragEvent<HTMLDivElement>, node: ComposerAttributeNode) => {
        e.preventDefault();
        e.stopPropagation();
        const position = getDropPosition(e, node);
        if (!position) {
            e.dataTransfer.dropEffect = 'none';
            setAttributeDropTarget(null);
            return;
        }
        e.dataTransfer.dropEffect = 'move';
        setAttributeDropTarget((current) => (current?.id === node.id && current.position === position ? current : { id: node.id, position }));
    };

    const dragLeaveHandler = (event: React.DragEvent<HTMLDivElement>, node: ComposerAttributeNode) => {
        if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return;
        setAttributeDropTarget((current) => (current?.id === node.id ? null : current));
    };

    const dragEndHandler = () => {
        setDraggedAttributeId(null);
        setAttributeDropTarget(null);
    };

    const dropHandler = (e: React.DragEvent<HTMLDivElement>, node: ComposerAttributeNode | null) => {
        e.preventDefault();
        e.stopPropagation();
        const draggedNodeId = e.dataTransfer.getData(COMPOSER_ATTRIBUTE_DRAG_TYPE) || e.dataTransfer.getData('text/plain');
        const requestedPosition = node && attributeDropTarget?.id === node.id ? attributeDropTarget.position : undefined;
        const updatedAttributes = moveComposerAttributeNode(currentAttributes, draggedNodeId, node?.id ?? null, requestedPosition);
        dragEndHandler();
        if (updatedAttributes === currentAttributes) return;

        updatedAttributes.forEach((updatedNode, index) => {
            const currentNode = currentAttributes[index];
            if (
                currentNode.parentID === updatedNode.parentID &&
                currentNode.parentIDNumeric === updatedNode.parentIDNumeric &&
                currentNode.parentType === updatedNode.parentType &&
                currentNode.displayOrder === updatedNode.displayOrder
            ) {
                return;
            }
            updateNode(updatedNode.id, updatedNode);
            updateAttribute(updatedNode);
        });
        const movedNode = updatedAttributes.find((attribute) => attribute.id === draggedNodeId);
        if (movedNode) {
            setSelectedIds([movedNode.id]);
            selectAttributes([movedNode]);
            selectOptions(movedNode.options[0] ? [movedNode.options[0]] : []);
        }
        void validateAttributes();
    };

    // Removed problematic useEffect that was causing "Rendered fewer hooks" error
    // This was auto-selecting first attribute but interfered with manual selection logic

    // The stored flag is inverted: a row draws its children when `expanded` is false, as ComposerAttributeTreeElement reads it.
    // Only groups and steps own it: attribute rows have their options in the next column.
    const setBranchesExpanded = (expanded: boolean) => {
        filteredData.forEach((node) => {
            if (node.type !== ComposerAttributeType.Attribute) updateNode(node.id, { expanded: !expanded });
        });
    };

    const renderNodeChildren = (node: ComposerAttributeNode, level: number): React.ReactNode[] => {
        const children = getNodeChildren(node).sort((left, right) => sortByDisplay(filteredData, left, right));
        return children.map((child) => {
            const descendants = child.type === ComposerAttributeType.Attribute ? [] : renderNodeChildren(child, level + 1);
            return (
                <ComposerAttributeTreeElement
                    key={child.id}
                    attribute={child}
                    type={child.type}
                    hasLink={child.links.length > 0}
                    selected={selectedIds.includes(child.id)}
                    dragging={draggedAttributeId === child.id}
                    dropPosition={attributeDropTarget?.id === child.id ? attributeDropTarget.position : undefined}
                    shift={level}
                    expanded={child.expanded}
                    onSelect={nodeSelectHandler}
                    onMoveDown={() => moveNodeHandler(child, 'down')}
                    onMoveUp={() => moveNodeHandler(child, 'up')}
                    onDragStart={(event) => dragStartHandler(event, child)}
                    onDragEnd={dragEndHandler}
                    onDragOver={(event) => dragOverHandler(event, child)}
                    onDragLeave={(event) => dragLeaveHandler(event, child)}
                    onDrop={(event) => dropHandler(event, child)}
                    onDoubleClick={() => {
                        selectSingleAttribute(child);
                        setAttributeFace('settings');
                    }}
                    onAction={onAttributeRowAction}
                    onVisibilityClick={toggleAttributeEnabled}
                    onToggleExpand={() => updateNode(child.id, { ...child, expanded: !child.expanded })}
                >
                    {descendants.length > 0 ? descendants : undefined}
                </ComposerAttributeTreeElement>
            );
        });
    };

    return (
        <ComposerPanel aria-label={T._('Configurator structure', 'Admin')}>
            <ComposerToolbar aria-label={T._('Product commands', 'Admin')}>
                <PrimaryButton onClick={() => onAttributeButtonClick('add')}>
                    <Plus />
                    {T._('Add', 'Admin')}
                </PrimaryButton>
                <ZTooltip title={T._('Assign a screenshot', 'Admin')} position='bottom' addContainer>
                    <ComposerIconButton aria-label={T._('Assign a screenshot', 'Admin')} onClick={() => onAttributeButtonClick('screenshot')}>
                        <Screenshot />
                    </ComposerIconButton>
                </ZTooltip>
                <ZTooltip
                    title={canCombineAttributes ? T._('Merge attributes', 'Admin') : T._('Merging needs at least two attributes', 'Admin')}
                    position='bottom'
                    addContainer
                >
                    <ComposerIconButton
                        aria-label={T._('Merge attributes', 'Admin')}
                        disabled={!canCombineAttributes}
                        onClick={() => onAttributeButtonClick('merge')}
                    >
                        <Merge />
                    </ComposerIconButton>
                </ZTooltip>
                <ZTooltip
                    title={
                        canCombineAttributes ? T._('Copy options between attributes', 'Admin') : T._('Copying options needs at least two attributes', 'Admin')
                    }
                    position='bottom'
                    addContainer
                >
                    <ComposerIconButton
                        aria-label={T._('Copy options between attributes', 'Admin')}
                        disabled={!canCombineAttributes}
                        onClick={() => onAttributeButtonClick('copy')}
                    >
                        <Copy />
                    </ComposerIconButton>
                </ZTooltip>
            </ComposerToolbar>
            <ComposerSearchRow>
                <ComposerSearch
                    aria-label={T._('Search structure', 'Admin')}
                    placeholder={T._('Filter the tree…', 'Admin')}
                    type='search'
                    value={filter}
                    onChange={(event) => setFilter(event.target.value)}
                />
            </ComposerSearchRow>
            <ComposerTreeToolsRow>
                <ComposerLinkButton onClick={() => setBranchesExpanded(false)}>
                    <Minus />
                    {T._('Collapse all', 'Admin')}
                </ComposerLinkButton>
                <ComposerLinkButton onClick={() => setBranchesExpanded(true)}>
                    <Plus />
                    {T._('Expand all', 'Admin')}
                </ComposerLinkButton>
            </ComposerTreeToolsRow>
            <TreeDropSurface
                $rootDropTarget={draggedAttributeId !== null && attributeDropTarget?.id === null}
                onDragOver={(event) => {
                    if (!draggedAttributeId) return;
                    event.preventDefault();
                    event.dataTransfer.dropEffect = 'move';
                    setAttributeDropTarget({ id: null });
                }}
                onDragLeave={(event) => {
                    if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return;
                    setAttributeDropTarget((current) => (current?.id === null ? null : current));
                }}
                onDrop={(event) => dropHandler(event, null)}
                role='tree'
            >
                {nodes.length === 0 ? (
                    <ComposerEmptyState>
                        {filter.trim() ? T._(`Nothing matches “${filter.trim()}”.`, 'Admin') : T._('Nothing here yet — Add starts one.', 'Admin')}
                    </ComposerEmptyState>
                ) : (
                    <ComposerList>
                        {nodes.map((node) => {
                            const children = getNodeChildren(node);
                            const sortedChildren = children.sort((a, b) => sortByDisplay(children, a, b));

                            return (
                                <ComposerAttributeTreeElement
                                    key={node.id}
                                    attribute={node}
                                    selected={selectedIds.includes(node.id)}
                                    dragging={draggedAttributeId === node.id}
                                    dropPosition={attributeDropTarget?.id === node.id ? attributeDropTarget.position : undefined}
                                    type={node.type}
                                    hasLink={node.links && node.links.length > 0}
                                    onSelect={nodeSelectHandler}
                                    onDragStart={(e) => dragStartHandler(e, node)}
                                    onDragEnd={dragEndHandler}
                                    onDragOver={(event) => dragOverHandler(event, node)}
                                    onDragLeave={(event) => dragLeaveHandler(event, node)}
                                    onDrop={(e) => dropHandler(e, node)}
                                    expanded={node.expanded}
                                    onMoveDown={(node) => {
                                        moveNodeHandler(node, 'down');
                                    }}
                                    onMoveUp={(node) => {
                                        moveNodeHandler(node, 'up');
                                    }}
                                    onDoubleClick={() => {
                                        selectSingleAttribute(node);
                                        setAttributeFace('settings');
                                    }}
                                    onAction={onAttributeRowAction}
                                    onVisibilityClick={toggleAttributeEnabled}
                                    onToggleExpand={(node) => {
                                        node.expanded = !node.expanded;
                                        updateNode(node.id, {
                                            ...node
                                        });
                                    }}
                                >
                                    {node.type !== ComposerAttributeType.Attribute && sortedChildren.length > 0 ? renderNodeChildren(node, 1) : undefined}
                                </ComposerAttributeTreeElement>
                            );
                        })}
                        {nodes.length > 1 && (
                            <TreeHint>
                                {isMacPlatform()
                                    ? T._('⇧click for a range · ⌘click one by one', 'Admin')
                                    : T._('Shift+click for a range · Ctrl/Cmd+click one by one', 'Admin')}
                            </TreeHint>
                        )}
                    </ComposerList>
                )}
            </TreeDropSurface>
        </ComposerPanel>
    );
};

export default ComposerAttributes;
